import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { sanitize } from "@/lib/sanitize";
import { Package, WhatsappLogo, FileText, Clock, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const fmtM = (n) => n ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : "$0.00";

export default function ProductPublicView() {
  const { linkId } = useParams();
  const [product, setProduct] = useState(null);
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(0);
  const [imgUrls, setImgUrls] = useState({});
  const [company, setCompany] = useState({ name: "", logo_path: null });
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products/share/${linkId}`)
      .then(r => { setProduct(r.data.product); setLink(r.data.link); })
      .catch(e => {
        const status = e.response?.status;
        if (status === 410) setError("Este link ha expirado.");
        else if (status === 404) setError("Link no encontrado.");
        else setError("Error al cargar el producto.");
      })
      .finally(() => setLoading(false));
    // Load company config
    axios.get(`${API}/products/config/company/logo-file`, { responseType: "blob" })
      .then(r => setLogoUrl(URL.createObjectURL(r.data)))
      .catch(() => {});
    axios.get(`${API}/products/config/company`)
      .then(r => setCompany(r.data))
      .catch(() => {});
  }, [linkId]);

  // Load public images
  useEffect(() => {
    if (!product?.images?.length) return;
    product.images.forEach(img => {
      if (imgUrls[img.id]) return;
      axios.get(`${API}/products/public-files/${img.storage_path}`, { responseType: "blob" })
        .then(r => {
          const url = URL.createObjectURL(r.data);
          setImgUrls(prev => ({ ...prev, [img.id]: url }));
        })
        .catch(() => {});
    });
  }, [product?.images]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center"><Package size={48} className="mx-auto text-slate-300 animate-pulse mb-3" /><p className="text-slate-400">Cargando producto...</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center bg-white border rounded-2xl p-10 max-w-md mx-4">
        <Warning size={48} className="mx-auto text-amber-500 mb-3" />
        <p className="text-lg font-bold text-slate-800 mb-2">{error}</p>
        <p className="text-sm text-slate-400">Contacta al vendedor para un nuevo enlace.</p>
      </div>
    </div>
  );

  const p = product;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-8 w-auto" /> : <Package size={24} weight="fill" className="text-blue-600" />}
            {!logoUrl && company.name && <span className="font-bold text-slate-800">{company.name}</span>}
          </div>
          {link?.created_by_name && <span className="text-xs text-slate-400">Compartido por {link.created_by_name}</span>}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-[4/3] max-h-[400px] bg-white border rounded-2xl flex items-center justify-center overflow-hidden mb-3">
              {p.images?.length > 0 && imgUrls[p.images[selectedImg]?.id]
                ? <img src={imgUrls[p.images[selectedImg]?.id]} alt={p.name} className="w-full h-full object-contain" />
                : <Package size={64} className="text-slate-200" />
              }
            </div>
            {p.images?.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {p.images.map((img, i) => (
                  <div key={img.id} onClick={() => setSelectedImg(i)}
                    className={`w-16 h-12 bg-white border rounded-lg overflow-hidden cursor-pointer ${i === selectedImg ? "border-blue-400 ring-2 ring-blue-100" : ""}`}>
                    {imgUrls[img.id] ? <img src={imgUrls[img.id]} alt="" className="w-full h-full object-cover" /> : <Package size={12} className="text-slate-200 mx-auto mt-3" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-5">
            <div>
              <span className="text-blue-600 font-mono text-sm font-semibold bg-blue-50 px-2.5 py-1 rounded">{p.sku}</span>
              {p.brand && <span className="ml-2 text-xs text-slate-400">{p.brand}</span>}
            </div>
            <h1 className="text-3xl font-extrabold text-slate-900 leading-tight break-words">{p.name}</h1>
            {p.description && <div className="text-slate-500 leading-relaxed prose prose-sm max-w-none overflow-hidden break-words [&_*]:max-w-full" dangerouslySetInnerHTML={sanitize(p.description)} />}

            {/* Prices - Only PL and PPR (no PSC for clients) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white border rounded-xl p-4 text-center">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Precio de Lista</p>
                <p className="text-2xl font-extrabold text-slate-900">{fmtM(p.precio_lista)}</p>
              </div>
              {p.precio_promocion > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-1">Precio Promocion</p>
                  <p className="text-2xl font-extrabold text-emerald-700">{fmtM(p.precio_promocion)}</p>
                </div>
              )}
            </div>

            {/* Variants */}
            {p.variants?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Variantes</p>
                <div className="flex gap-2 flex-wrap">
                  {p.variants.map((v, i) => <span key={i} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600">{v.name || v}</span>)}
                </div>
              </div>
            )}

            {/* Promo */}
            {p.promotion?.active && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <span className="bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-lg uppercase">PROMO -{p.promotion.discount_pct}%</span>
                <p className="text-sm font-semibold text-red-700 mt-2">{p.promotion.description}</p>
              </div>
            )}

            {/* Specs */}
            {p.specs?.length > 0 && (
              <div className="bg-white border rounded-xl p-5">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Especificaciones</h3>
                <div className="divide-y">
                  {p.specs.map((s, i) => (
                    <div key={i} className="flex justify-between py-2 text-sm">
                      <span className="text-slate-500">{s.key}</span>
                      <span className="text-slate-800 font-semibold">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {p.documents?.length > 0 && (
              <div className="bg-white border rounded-xl p-5">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Documentos</h3>
                <div className="space-y-2">
                  {p.documents.map(d => (
                    <a key={d.id} href={`${API}/products/public-files/${d.storage_path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors">
                      <FileText size={18} className="text-blue-500" />
                      <span className="text-sm text-slate-700">{d.original_filename}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {link?.expires_at && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock size={14} /> Este enlace expira: {new Date(link.expires_at).toLocaleString("es-MX")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
